import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Check, Target, X } from 'lucide-react';
import type { ChatSuggestedEvent, ChatSuggestedGoal } from '@/lib/api/chatCreation.service';

interface GoalReviewCardProps {
  selectedEvents: ChatSuggestedEvent[];
  goalSuggestions: Record<string, ChatSuggestedGoal[]>;
  onConfirm?: (selectedGoalTemplateIdsByEvent: Record<string, number[]>) => void;
  onSkip?: () => void;
}

export const GoalReviewCard: React.FC<GoalReviewCardProps> = ({
  selectedEvents,
  goalSuggestions,
  onConfirm,
  onSkip,
}) => {
  const initialSelection = useMemo(() => {
    const value: Record<string, Set<number>> = {};
    for (const event of selectedEvents) {
      const suggestions = goalSuggestions[event.id] ?? [];
      value[event.id] = new Set(
        suggestions
          .filter((goal) => goal.selectedByDefault !== false && typeof goal.goalTemplateId === 'number')
          .map((goal) => goal.goalTemplateId as number),
      );
    }
    return value;
  }, [selectedEvents, goalSuggestions]);

  const [selected, setSelected] = useState<Record<string, Set<number>>>(initialSelection);

  const hasAnySuggestions = selectedEvents.some((event) =>
    (goalSuggestions[event.id] ?? []).some((goal) => typeof goal.goalTemplateId === 'number'),
  );
  if (!hasAnySuggestions) return null;

  const toggleGoal = (eventId: string, goalTemplateId: number) => {
    setSelected((prev) => {
      const next = { ...prev };
      const eventSet = new Set(next[eventId] ?? []);
      if (eventSet.has(goalTemplateId)) {
        eventSet.delete(goalTemplateId);
      } else {
        eventSet.add(goalTemplateId);
      }
      next[eventId] = eventSet;
      return next;
    });
  };

  const handleConfirm = () => {
    const payload: Record<string, number[]> = {};
    for (const event of selectedEvents) {
      const suggestions = goalSuggestions[event.id] ?? [];
      if (suggestions.length === 0) continue;
      payload[event.id] = Array.from(selected[event.id] ?? []);
    }

    onConfirm?.(payload);
  };

  return (
    <Card className="border-emerald-200 bg-emerald-50/60 max-w-xl w-full">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <Target className="h-5 w-5 text-emerald-700" />
          Suggested Goals
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          Choose goals to auto-add for each event. Linked goal tasks will be included automatically.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {selectedEvents.map((event) => {
          const goals = (goalSuggestions[event.id] ?? []).filter((goal) => typeof goal.goalTemplateId === 'number');
          if (goals.length === 0) return null;

          return (
            <div key={event.id} className="space-y-2 rounded-md border bg-background p-2">
              <p className="text-sm font-medium">{event.title}</p>
              {goals.map((goal) => {
                const goalTemplateId = goal.goalTemplateId as number;
                return (
                  <div key={goal.id} className="flex items-start gap-2">
                    <input
                      type="checkbox"
                      aria-label={`Select goal ${goal.title} for ${event.title}`}
                      checked={selected[event.id]?.has(goalTemplateId) ?? false}
                      onChange={() => toggleGoal(event.id, goalTemplateId)}
                      className="mt-0.5 h-4 w-4 rounded border border-input"
                    />
                    <div>
                      <p className="text-sm">{goal.title}</p>
                      <p className="text-[11px] text-muted-foreground">
                        Suggested organizer goal
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </CardContent>
      <CardFooter className="flex gap-2 pt-2">
        {onConfirm && (
          <Button size="sm" onClick={handleConfirm} className="flex-1 bg-emerald-600 text-white hover:bg-emerald-700">
            <Check className="h-4 w-4 mr-1" />
            Confirm Goals
          </Button>
        )}
        {onSkip && (
          <Button size="sm" variant="outline" onClick={onSkip} className="flex-1">
            <X className="h-4 w-4 mr-1" />
            Skip Goals
          </Button>
        )}
      </CardFooter>
    </Card>
  );
};
